/**
 * Merge brush/eraser strokes into polygon geometry using polygon-clipping.
 * Returns a new polygon that includes brush additions and excludes eraser areas.
 */
import polygonClipping from 'polygon-clipping';

const CIRCLE_SEGMENTS = 24;
const MIN_RADIUS = 1;
const ERASER_INTERPOLATION_STEP = 5;
/** Min spacing between brush samples when merging (canvas px); skips redundant unions for dense strokes. */
const BRUSH_MERGE_MIN_POINT_SPACING_FACTOR = 0.42;

/** Create a polygon approximating a circle */
function circleToPolygon(cx, cy, r) {
  const radius = Math.max(Number(r) || 0, MIN_RADIUS);
  const ring = [];
  for (let i = 0; i <= CIRCLE_SEGMENTS; i++) {
    const a = (i / CIRCLE_SEGMENTS) * Math.PI * 2;
    ring.push([cx + radius * Math.cos(a), cy + radius * Math.sin(a)]);
  }
  return [ring];
}

/**
 * Drop intermediate polyline samples closer than `minSpacing` (Manhattan step along path).
 * Always keeps first and last point so stroke caps stay correct.
 */
function decimatePolylinePointsForMerge(points, minSpacing) {
  if (!points?.length) return [];
  if (points.length <= 2) return points;
  const xy = (p) => {
    const x = Number(p.x ?? p[0]);
    const y = Number(p.y ?? p[1]);
    return [x, y];
  };
  const out = [points[0]];
  let [lx, ly] = xy(points[0]);
  const spacing = Math.max(MIN_RADIUS, minSpacing);
  for (let i = 1; i < points.length - 1; i++) {
    const [x, y] = xy(points[i]);
    if (Math.hypot(x - lx, y - ly) >= spacing) {
      out.push(points[i]);
      lx = x;
      ly = y;
    }
  }
  const last = points[points.length - 1];
  if (out[out.length - 1] !== last) out.push(last);
  return out;
}

/** Convert points array to polygon-clipping format (closed ring) */
function pointsToRing(points) {
  if (!points?.length) return null;
  const ring = points.map((p) => {
    const x = Array.isArray(p) ? p[0] : p.x;
    const y = Array.isArray(p) ? p[1] : p.y;
    return [Number(x), Number(y)];
  });
  if (ring.length < 3) return null;
  if (ring[0][0] !== ring[ring.length - 1][0] || ring[0][1] !== ring[ring.length - 1][1]) {
    ring.push([ring[0][0], ring[0][1]]);
  }
  return [ring];
}

/** Convert polygon points to polygon-clipping format */
function toClippingFormat(points) {
  const ring = pointsToRing(points);
  return ring ? [ring] : null;
}

function expandEraserPoints(points, step = ERASER_INTERPOLATION_STEP) {
  if (!points?.length) return [];
  const out = [];
  for (let i = 0; i < points.length; i++) {
    out.push(points[i]);
    if (i < points.length - 1) {
      const a = points[i];
      const b = points[i + 1];
      const ax = a.x ?? a[0];
      const ay = a.y ?? a[1];
      const bx = b.x ?? b[0];
      const by = b.y ?? b[1];
      const dx = bx - ax;
      const dy = by - ay;
      const dist = Math.hypot(dx, dy) || 1;
      const n = Math.max(1, Math.floor(dist / step));
      for (let j = 1; j < n; j++) {
        const t = j / n;
        out.push({ x: ax + dx * t, y: ay + dy * t });
      }
    }
  }
  return out;
}

/**
 * Apply brush unions + eraser differences to an existing clipping multipolygon.
 * @param {unknown} result polygon-clipping MultiPolygon
 * @param {number} scale natural → canvas (divide coords when returning)
 */
function applyStrokesToMultipolygon(result, brushStrokes, eraserStrokes, scale) {
  let r = result;
  try {
    for (const stroke of brushStrokes || []) {
      if (!stroke?.points?.length) continue;
      const radius = Math.max((stroke.size ?? 10) / 2, MIN_RADIUS);
      const minSpacing = Math.max(MIN_RADIUS * 1.5, radius * BRUSH_MERGE_MIN_POINT_SPACING_FACTOR);
      const thinPoints = decimatePolylinePointsForMerge(stroke.points, minSpacing);
      for (const p of thinPoints) {
        const cx = p.x ?? p[0];
        const cy = p.y ?? p[1];
        const circle = circleToPolygon(cx, cy, radius);
        r = polygonClipping.union(r, circle);
        if (!r || r.length === 0) return null;
      }
    }

    for (const stroke of eraserStrokes || []) {
      if (!stroke?.points?.length) continue;
      const baseSize = Math.max((stroke.size ?? 10) / 2, MIN_RADIUS);
      const points = expandEraserPoints(stroke.points);
      for (const p of points) {
        const cx = p.x ?? p[0];
        const cy = p.y ?? p[1];
        const circle = circleToPolygon(cx, cy, baseSize);
        try {
          r = polygonClipping.difference(r, circle);
        } catch (err) {
          console.warn('Eraser difference failed for one point, skipping:', err?.message);
          continue;
        }
        if (!r || r.length === 0) return null;
      }
    }

    const flattened = flattenResultWithHoles(r);
    if (!flattened) return null;
    const toNatural = (ring) => ring.map(([x, y]) => [x / scale, y / scale]);
    if (flattened.rings && flattened.rings.length > 0) {
      return { rings: flattened.rings.map(toNatural) };
    }
    return null;
  } catch (e) {
    console.warn('Polygon merge failed:', e);
    return null;
  }
}

/**
 * Union all rings first, then merge brush/eraser. Use for brush on multi-island masks so
 * overlapping paths are not drawn as even-odd holes (which looked like "negative" brush).
 * @param {Array<Array>} rings - list of rings [[x,y],...] in image/natural space
 */
export function mergeMultiRingPolygonWithStrokes(rings, brushStrokes, eraserStrokes, scale = 1) {
  if (!rings?.length) return null;
  let combined = null;
  for (const ring of rings) {
    if (!ring || ring.length < 3) continue;
    const baseCanvas = ring.map((p) => {
      const x = Array.isArray(p) ? p[0] : p.x;
      const y = Array.isArray(p) ? p[1] : p.y;
      return [Number(x) * scale, Number(y) * scale];
    });
    const poly = toClippingFormat(baseCanvas);
    if (!poly) continue;
    combined = combined ? polygonClipping.union(combined, poly) : poly;
  }
  if (!combined) return null;
  return applyStrokesToMultipolygon(combined, brushStrokes, eraserStrokes, scale);
}

/**
 * Merge brush and eraser strokes into the base polygon.
 * All coordinates in same space (canvas).
 * @param {Array} basePoints - [[x,y],...] - will be scaled from natural to canvas if scale provided
 * @param {Array} brushStrokes - [{ points: [{x,y}], size }] - in canvas coords
 * @param {Array} eraserStrokes - [{ points: [{x,y}], size }] - in canvas coords
 * @param {number} scale - natural to canvas scale (canvas = natural * scale)
 * @returns {{ points: Array, holes?: Array } | { rings: Array } | null} - merged polygon in natural coords
 */
export function mergePolygonWithStrokes(basePoints, brushStrokes, eraserStrokes, scale = 1) {
  const baseCanvas = basePoints.map((p) => {
    const x = Array.isArray(p) ? p[0] : p.x;
    const y = Array.isArray(p) ? p[1] : p.y;
    return [Number(x) * scale, Number(y) * scale];
  });
  const base = toClippingFormat(baseCanvas);
  if (!base) return null;
  return applyStrokesToMultipolygon(base, brushStrokes, eraserStrokes, scale);
}

/**
 * Compute axis-aligned bounding box from polygon points.
 * @param {Array} points - [[x,y],...] or polygon object { points, holes? }
 * @returns {[number, number, number, number]} [minX, minY, maxX, maxY] or null
 */
export function computePolygonBbox(poly) {
  let points = [];
  if (poly?.points && Array.isArray(poly.points)) {
    points = poly.points;
  } else if (Array.isArray(poly)) {
    const flat = poly.flat(2);
    for (let i = 0; i < flat.length - 1; i += 2) {
      points.push([Number(flat[i]), Number(flat[i + 1])]);
    }
  }
  if (points.length < 2) return null;
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const p of points) {
    const x = Array.isArray(p) ? p[0] : p.x;
    const y = Array.isArray(p) ? p[1] : p.y;
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
  }
  return [minX, minY, maxX, maxY];
}

/**
 * Flatten polygon-clipping result (MultiPolygon) into a flat list of rings.
 * MultiPolygon = [polygon, ...], each polygon = [ring1, ring2, ...]
 */
function flattenResultWithHoles(multiPolygon) {
  if (!multiPolygon?.length) return null;
  const rings = [];
  for (const polygon of multiPolygon) {
    if (!polygon?.length) continue;
    for (const ring of polygon) {
      if (ring?.length >= 3) {
        rings.push(ring.map((p) => [p[0], p[1]]));
      }
    }
  }
  if (!rings.length) return null;
  return { rings };
}

function closeRing(ring) {
  if (!ring?.length) return ring;
  const first = ring[0];
  const last = ring[ring.length - 1];
  if (first[0] === last[0] && first[1] === last[1]) return ring;
  return [...ring, [first[0], first[1]]];
}

function ringToCanvasPoints(ring) {
  return (ring ?? [])
    .map((p) => {
      if (Array.isArray(p)) return [Number(p[0]), Number(p[1])];
      return [Number(p?.x ?? p?.[0]), Number(p?.y ?? p?.[1])];
    })
    .filter((p) => Number.isFinite(p[0]) && Number.isFinite(p[1]));
}

function getRingCentroid(ring) {
  const pts = ringToCanvasPoints(ring);
  if (!pts.length) return null;
  let sx = 0;
  let sy = 0;
  for (const [x, y] of pts) {
    sx += x;
    sy += y;
  }
  return [sx / pts.length, sy / pts.length];
}

function pointInRing(point, ring) {
  // Ray casting algorithm; expects ring as [[x,y],...], not necessarily closed.
  if (!point || !ring || ring.length < 3) return false;
  const [px, py] = point;
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i][0];
    const yi = ring[i][1];
    const xj = ring[j][0];
    const yj = ring[j][1];
    const intersect = yi > py !== yj > py && px < ((xj - xi) * (py - yi)) / (yj - yi + 1e-10) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

/**
 * Convert a canvas polygon ring list that is intended to be rendered with `evenodd`
 * into a polygon-clipping MultiPolygon with explicit hole rings.
 *
 * This is necessary because `polygonMerge` uses polygon-clipping ops that require
 * proper hole assignment; naively unioning each ring as a separate polygon breaks
 * holes and causes erased areas to "reset".
 *
 * @param {Array<Array>} rings list of rings in natural/image coords ([[x,y],...], ...)
 * @param {number} scale natural → canvas scale factor
 */
function ringsToEvenOddClippingMultiPolygon(rings, scale) {
  if (!Array.isArray(rings) || rings.length === 0) return null;

  // Normalize points once in natural coords for containment classification.
  const normalizedNatural = rings.map((ring) => {
    const pts = (ring ?? [])
      .map((p) => {
        if (Array.isArray(p)) return [Number(p[0]), Number(p[1])];
        return [Number(p?.x ?? p?.[0]), Number(p?.y ?? p?.[1])];
      })
      .filter((p) => Number.isFinite(p[0]) && Number.isFinite(p[1]));
    return pts;
  });

  const testPoints = normalizedNatural.map((ring) => getRingCentroid(ring));
  if (testPoints.some((p) => p == null)) return null;

  const depths = normalizedNatural.map((ring, i) => {
    const pt = testPoints[i];
    if (!pt) return 0;
    let count = 0;
    for (let j = 0; j < normalizedNatural.length; j++) {
      if (i === j) continue;
      const other = normalizedNatural[j];
      if (other.length < 3) continue;
      if (pointInRing(pt, other)) count += 1;
    }
    return count;
  });

  const outers = [];
  for (let i = 0; i < normalizedNatural.length; i++) {
    if (normalizedNatural[i]?.length >= 3 && depths[i] % 2 === 0) outers.push(i);
  }
  if (outers.length === 0) return null;

  const holesByOuterIndex = new Map();
  for (const outerIdx of outers) holesByOuterIndex.set(outerIdx, []);

  // Assign each odd-depth ring (hole) to the closest enclosing outer (max depth).
  for (let i = 0; i < normalizedNatural.length; i++) {
    if (normalizedNatural[i]?.length < 3) continue;
    if (depths[i] % 2 === 0) continue; // outer, not hole

    const pt = testPoints[i];
    if (!pt) continue;

    const containingOuters = outers
      .filter((outerIdx) => pointInRing(pt, normalizedNatural[outerIdx]))
      .sort((a, b) => depths[b] - depths[a]); // deepest/closest first

    if (containingOuters.length === 0) {
      // If we can't assign, skip to avoid corrupting geometry.
      continue;
    }

    const parentOuter = containingOuters[0];
    holesByOuterIndex.get(parentOuter)?.push(i);
  }

  // Build clipping polygons in canvas coords with explicit holes.
  const multipolygons = [];
  for (const outerIdx of outers) {
    const outerCanvas = closeRing(normalizedNatural[outerIdx].map(([x, y]) => [x * scale, y * scale]));
    const holeIdxs = holesByOuterIndex.get(outerIdx) ?? [];
    const holesCanvas = holeIdxs
      .map((hi) =>
        closeRing(normalizedNatural[hi].map(([x, y]) => [x * scale, y * scale]))
      )
      .filter((r) => r?.length >= 4);

    multipolygons.push([outerCanvas, ...holesCanvas]);
  }

  // polygon-clipping expects MultiPolygon = [polygon, polygon, ...]
  return multipolygons;
}

/**
 * Merge brush/eraser strokes into a multi-ring polygon while respecting
 * hole parity (evenodd semantics). This prevents eraser edits from "resetting"
 * holes when subsequent strokes are applied.
 */
export function mergeMultiRingPolygonWithStrokesRespectHoles(
  rings,
  brushStrokes,
  eraserStrokes,
  scale = 1
) {
  if (!rings?.length) return null;
  const base = ringsToEvenOddClippingMultiPolygon(rings, scale);
  if (!base) return null;
  return applyStrokesToMultipolygon(base, brushStrokes, eraserStrokes, scale);
}
